import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { CardActions } from "@remo-co/ui-core/src/components/CardActions";
import { CardContent } from "@remo-co/ui-core/src/components/CardContent";
import { FormControl } from "@remo-co/ui-core/src/components/FormControl";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import useAuth from "modules/auth/hooks/useAuth";
import NavigateToForgetPassword from "modules/auth/authNavigation/NavigateToForgetPassword";
import { isValidEmailAddress } from "helpers/emailHelper";
import { useI18n } from "i18n";
import { useStyles } from "../../common/styles";

interface IInputField {
  error: boolean;
  value: string;
  message: string;
}

export interface Props {
  prefilledEmail: string;
  disableForgotPassword?: boolean;
}

const UsernamePasswordSignInForm = ({
  prefilledEmail,
  disableForgotPassword = false,
}: Props) => {
  const auth = useAuth();
  const { t } = useI18n(["common", "auth"]);
  const [isLoading, setLoading] = useState(false);
  const [emailField, setEmailField] = useState<IInputField>({
    error: false,
    value: prefilledEmail || "",
    message: "",
  });
  const [passwordField, setPasswordField] = useState<IInputField>({
    error: false,
    value: "",
    message: "",
  });
  const classes = useStyles();

  useEffect(() => {
    if (prefilledEmail && emailField.value !== prefilledEmail) {
      setEmailField({ ...emailField, value: prefilledEmail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledEmail]);

  const onFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === "email") {
      setEmailField({ value: e.target.value, error: false, message: "" });
    }

    if (e.target.name === "password") {
      setPasswordField({ value: e.target.value, error: false, message: "" });
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validate the values
    if (!emailField.value) {
      setEmailField({
        value: "",
        error: true,
        message: t("auth:enter.valid.email"),
      });
    } else if (!isValidEmailAddress(emailField.value)) {
      setEmailField({
        ...emailField,
        error: true,
        message: t("auth:enter.valid.email"),
      });
    }

    if (!passwordField.value) {
      setPasswordField({
        value: "",
        error: true,
        message: t("auth:enter.password"),
      });
    }

    if (!passwordField.value || !emailField.value) {
      return;
    }
    setLoading(true);
    await auth.signIn(emailField.value, passwordField.value);
    setLoading(false);
    setPasswordField({ ...passwordField, value: "" });
  };

  return (
    <>
      <form
        noValidate
        onSubmit={onSubmit}
        className="signin-form"
        data-testid="signin-form"
      >
        <CardContent className={classes.cardContent}>
          <FormControl
            className={classes.formControl}
            aria-describedby="email-text"
          >
            <Input
              id="email"
              name="email"
              fullWidth
              required
              autoFocus
              onChange={onFieldChange}
              inputProps={{ "data-testid": "email-input" }}
              label={t("auth:email.address")}
              size="sm"
              error={emailField.message}
              value={emailField.value}
            />
          </FormControl>
          <FormControl
            className={classes.formControl}
            aria-describedby="password-text"
          >
            <Input
              id="password"
              name="password"
              type="password"
              fullWidth
              required
              onChange={onFieldChange}
              inputProps={{ "data-testid": "password-input" }}
              label={t("auth:password")}
              size="sm"
              error={passwordField.message}
              value={passwordField.value}
              labelComponent={
                disableForgotPassword
                  ? undefined
                  : // eslint-disable-next-line react/no-unstable-nested-components
                    () => <NavigateToForgetPassword />
              }
            />
          </FormControl>
        </CardContent>
        <CardActions className="card-actions">
          <Button
            variant="primary"
            color="blue"
            fullWidth
            size="lg"
            type="submit"
          >
            {t("auth:login")}
          </Button>
        </CardActions>
      </form>
      {isLoading && (
        <div className="login-loading">
          <PageLoader message={t("wait")} />
        </div>
      )}
    </>
  );
};

export default UsernamePasswordSignInForm;
